/**
 * "Remove all devices" (board onboarding-r2) and the box's base skills (board learn-r1): the words and small reads the
 * Account card and the admin's box card use. Pure.
 *
 * Paseo can't drop one device, and its pairing link never expires, so removing devices re-keys the person's Paseo:
 * every phone, iPad and computer paired to that account stops reaching it and must pair again with a new link.
 */
import { ago } from './format'
import type { PersonView, SkillsState } from './schemas'

/** The daemon key in a Paseo pairing link (`#offer=<base64url JSON>`), or null. */
export function offerKey(url: string): string | null {
  const m = url.match(/#offer=([A-Za-z0-9_-]+)$/)
  if (!m) return null
  try {
    const b64 = m[1]!.replace(/-/g, '+').replace(/_/g, '/')
    const offer = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))) as { daemonPublicKeyB64?: unknown }
    return typeof offer.daemonPublicKeyB64 === 'string' && offer.daemonPublicKeyB64 ? offer.daemonPublicKeyB64 : null
  } catch {
    return null
  }
}

/**
 * The key as the Paseo app shows it when a link changes a saved computer's key: letters and digits only, in groups of
 * four, the first 8 and the last 8 (Paseo's host-confirmation.ts).
 */
export function keyFingerprint(url: string): string | null {
  const key = offerKey(url)
  if (!key) return null
  const n = key.replace(/[^A-Za-z0-9]/g, '')
  const group = (v: string) => v.replace(/(.{4})/g, '$1 ').trim()
  return n.length <= 16 ? group(n) : `${group(n.slice(0, 8))} … ${group(n.slice(-8))}`
}

/** Names of the others on the same Linux user as `me`: everyone whose devices go too. */
export function sharingWith(people: PersonView[], meId: string): string[] {
  const user = people.find((p) => p.id === meId)?.linuxUser
  if (!user) return []
  return people.filter((p) => p.id !== meId && p.linuxUser === user).map((p) => p.name ?? p.email)
}

const list = (names: string[]) => (names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`)

/** The confirm question on the person's own Account page. */
export function removeQuestion(others: string[]): string {
  const shared = others.length ? ` ${list(others)} ${others.length === 1 ? 'shares' : 'share'} this account, so their devices go too.` : ''
  return `Every phone, iPad and computer paired to your computer stops working now and must pair again with a new link.${shared} This goes in the audit as you.`
}

/** The confirm question on the admin's box card, for one Linux user. */
export function removeQuestionFor(user: string, people: string[]): string {
  const who = people.length ? ` (${list(people)})` : ''
  return `Every device paired to ${user}${who} stops working now and must pair again from the dashboard. This goes in the audit as you.`
}

/** Why removing devices didn't work, for people. */
export function rotateProblem(e: unknown): string {
  // An ApiError from src/infra/api.ts: its code says which.
  if (e instanceof Error && 'code' in e) {
    if (e.code === 'agent_too_old') return "Your computer's software needs an update before it can remove devices. We're on it; try again later."
    if (e.code === 'agent_offline') return "Your computer isn't connected right now. Try again in a minute."
    if (e.code === 'agent_timeout') return "Your computer didn't answer in time. Your devices may already be removed; check again in a minute."
    if (e.code === 'no_box') return "Your computer isn't set up yet, so there's nothing paired to remove."
    if (e.code === 'no_user') return "Your own space on the computer isn't set up yet, so there's nothing paired to remove."
    if (e.code === 'agent_error') return `Your computer couldn't do it: ${e.message}`
    return e.message
  }
  return "We couldn't remove your devices just now. Try again in a moment."
}

export interface BoxUserRow {
  user: string
  /** Names (or emails) of everyone on this Linux user. */
  people: string[]
  removedAt: string | null
}

/** One row per Linux user on the live box, the founder's first, from the people view. */
export function boxUserRows(people: PersonView[], founderUser: string | null): BoxUserRow[] {
  const rows = new Map<string, BoxUserRow>()
  for (const p of people) {
    if (!p.linuxUser) continue
    const r = rows.get(p.linuxUser) ?? { user: p.linuxUser, people: [], removedAt: null }
    r.people.push(p.name ?? p.email)
    if (p.devicesRemovedAt && (!r.removedAt || p.devicesRemovedAt > r.removedAt)) r.removedAt = p.devicesRemovedAt
    rows.set(p.linuxUser, r)
  }
  return [...rows.values()].sort((a, b) => (a.user === founderUser ? -1 : b.user === founderUser ? 1 : a.user.localeCompare(b.user)))
}

/** A restoration shows for a week, like the API's `base-skill-restored` finding. */
export const RESTORED_SHOWN_MS = 7 * 86_400_000

/** The box card's base-skills line: the commit, and the last restoration while it is recent. */
export function skillsWords(skills: SkillsState | null | undefined, now: number): { commit: string | null; restored: string | null } {
  if (!skills) return { commit: null, restored: null }
  const commit = skills.commit ? skills.commit.slice(0, 7) : null
  const at = skills.restoredAt ? Date.parse(skills.restoredAt) : NaN
  const recent = skills.restored.length > 0 && !Number.isNaN(at) && now - at <= RESTORED_SHOWN_MS
  return { commit, restored: recent ? `restored ${list(skills.restored)} ${ago(skills.restoredAt!, now)}` : null }
}
